import type { BillingConfigRead } from '../types/api';

/**
 * Paddle.js v2, injected on demand from Paddle's CDN (no npm package). Only
 * the calls the upgrade page makes are declared.
 */
const PADDLE_SCRIPT_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';

/** One Paddle.js event; `name` is e.g. `checkout.completed`, `checkout.closed`. */
export interface PaddleEvent {
  name?: string;
  data?: unknown;
}

export type PaddleEventHandler = (event: PaddleEvent) => void;

interface PaddleGlobal {
  Environment: { set: (environment: 'sandbox') => void };
  Initialize: (options: { token: string; eventCallback: PaddleEventHandler }) => void;
  Checkout: {
    open: (options: { transactionId: string }) => void;
    close: () => void;
  };
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

let paddlePromise: Promise<PaddleGlobal> | null = null;

/** Paddle.js allows one `Initialize`, so its callback forwards to the open checkout. */
let currentHandler: PaddleEventHandler | null = null;

function injectScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PADDLE_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error('The payment window could not load. Check your connection and try again.'));
    };
    document.head.appendChild(script);
  });
}

/**
 * Loads and initialises Paddle.js once per page (the promise is cached; a
 * failed load is forgotten so the next click retries). Needs billing enabled
 * with a client token.
 */
export function loadPaddle(config: BillingConfigRead): Promise<PaddleGlobal> {
  const token = config.client_token;
  if (!config.enabled || !token) {
    return Promise.reject(new Error('Billing is not open yet.'));
  }
  paddlePromise ??= injectScript()
    .then(() => {
      const paddle = window.Paddle;
      if (!paddle) throw new Error('The payment window could not load. Try again.');
      if (config.environment === 'sandbox') paddle.Environment.set('sandbox');
      paddle.Initialize({ token, eventCallback: (event) => currentHandler?.(event) });
      return paddle;
    })
    .catch((caught: unknown) => {
      paddlePromise = null;
      throw caught;
    });
  return paddlePromise;
}

/**
 * Opens Paddle's overlay on a transaction the API made for this account, and
 * routes the overlay's events to `onEvent` until the next checkout opens.
 * Call `loadPaddle` first.
 */
export async function openCheckout(
  transactionId: string,
  onEvent: PaddleEventHandler,
): Promise<void> {
  if (!paddlePromise) throw new Error('The payment window is not loaded yet. Try again.');
  const paddle = await paddlePromise;
  currentHandler = onEvent;
  paddle.Checkout.open({ transactionId });
}

/** Closes the overlay if Paddle.js is loaded. */
export function closeCheckout(): void {
  window.Paddle?.Checkout.close();
}
